import { ActivatedRoute } from "@angular/router";

export type BreadcrumbLabelResolver = (label: string, params: Record<string, unknown>) => string;

export class BreadcrumbItem {
  label!: string;
  url?: string;
}

/**
 * One entry of a route's `data.breadcrumb` array. A plain string links to the active route's own URL
 * (the historical behaviour, right for the last crumb); an object names the page an earlier crumb
 * should open, e.g. `{ label: "PHMS", url: "/phms/dashboard" }`.
 */
export type BreadcrumbRouteEntry = string | { label: string; url: string };

type BreadcrumbRouteSnapshot = {
  data: Record<string, unknown>;
  params: Record<string, unknown>;
  url: Array<{ path: string }>;
};

type BreadcrumbRoute = {
  children: BreadcrumbRoute[];
  firstChild: BreadcrumbRoute | null;
  parent: BreadcrumbRoute | null;
  snapshot: BreadcrumbRouteSnapshot;
};

export function buildBreadcrumbItems(
  route: ActivatedRoute,
  resolveLabel: BreadcrumbLabelResolver,
): BreadcrumbItem[] {
  const breadcrumbItems: BreadcrumbItem[] = [];

  addBreadcrumbs(route as BreadcrumbRoute, "", breadcrumbItems, resolveLabel);

  return breadcrumbItems;
}

function addBreadcrumbs(
  route: BreadcrumbRoute,
  parentUrl: string,
  breadcrumbItems: BreadcrumbItem[],
  resolveLabel: BreadcrumbLabelResolver,
): void {
  for (const child of route.children) {
    const childUrl = buildChildUrl(parentUrl, child.snapshot.url);
    const breadcrumbData = child.snapshot.data["breadcrumb"];

    if (breadcrumbData) {
      const entries = Array.isArray(breadcrumbData) ? breadcrumbData : [breadcrumbData];

      for (const entry of entries) {
        const parsed = parseEntry(entry);
        if (!parsed) {
          continue;
        }

        const breadcrumb = new BreadcrumbItem();
        breadcrumb.label = resolveLabel(parsed.label, collectParams(child));
        breadcrumb.url = parsed.url ?? childUrl;
        breadcrumbItems.push(breadcrumb);
      }
    }

    if (child.firstChild) {
      addBreadcrumbs(child, childUrl, breadcrumbItems, resolveLabel);
    }
  }
}

function parseEntry(entry: unknown): { label: string; url?: string } | null {
  if (typeof entry === "string") {
    return { label: entry };
  }

  if (entry && typeof entry === "object") {
    const candidate = entry as { label?: unknown; url?: unknown };
    if (typeof candidate.label === "string") {
      const url = typeof candidate.url === "string" && candidate.url.trim() ? candidate.url : undefined;
      return { label: candidate.label, url };
    }
  }

  return null;
}

function buildChildUrl(parentUrl: string, segments: Array<{ path: string }>): string {
  const routeSegment = segments.map((segment) => segment.path).filter(Boolean).join("/");

  if (!routeSegment) {
    return parentUrl || "/";
  }

  return `${parentUrl}/${routeSegment}`;
}

function collectParams(route: BreadcrumbRoute): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  const routeChain: BreadcrumbRoute[] = [];
  let currentRoute: BreadcrumbRoute | null = route;

  while (currentRoute) {
    routeChain.unshift(currentRoute);
    currentRoute = currentRoute.parent;
  }

  for (const routeInChain of routeChain) {
    Object.assign(params, routeInChain.snapshot.params);
  }

  return params;
}
